import { useEffect, useRef } from 'react';
import FeatherIcon from 'feather-icons-react';
import { AvatarLetter } from 'components/avatar';
import { trainingLicenseChangeFailed, trainingLicenseChanged } from 'constants/licenses';
import {
  SKILL_DIVE_NOT_ASSIGNED_WITHOUT_TRAINING,
  skillDiveLicenseChangeFailed,
  skillDiveLicenseChanged,
} from 'constants/skill_dive';
import {
  INVITE_OUTCOME_COPY,
  addedToTeamLine,
  invitationsNotSentLine,
  invitationsSentLine,
  madeManagerLine,
  managerChangeFailedLine,
  notAddedToTeamLine,
  notMadeManagerLine,
} from 'constants/invites';
import { ExistingUserChange, IInviteOutcome } from './types';
import {
  OutcomeContainer,
  OutcomeIntro,
  OutcomeLine,
  OutcomeLineText,
  OutcomeLines,
  OutcomeList,
  OutcomeReason,
  OutcomeRow,
  OutcomeSectionTitle,
  OutcomeTitle,
  OutcomeUser,
} from './styles';
import { colors } from 'constants/styles/colors';

// Worded as the users table words the same changes.
const describeChange = ({ kind, assign, result }: ExistingUserChange, teamName: string) => {
  if (kind === 'team') {
    return result === 'applied' ? addedToTeamLine(teamName) : notAddedToTeamLine(teamName);
  }
  if (kind === 'manager') {
    if (result === 'applied') return madeManagerLine(teamName);
    if (result === 'skipped') return notMadeManagerLine(teamName);
    return managerChangeFailedLine(teamName);
  }
  if (kind === 'training') {
    return result === 'applied'
      ? trainingLicenseChanged(assign)
      : trainingLicenseChangeFailed(assign);
  }
  if (result === 'applied') return skillDiveLicenseChanged(assign);
  if (result === 'skipped') return SKILL_DIVE_NOT_ASSIGNED_WITHOUT_TRAINING;
  return skillDiveLicenseChangeFailed(assign);
};

const LineText: React.FC<{ text: string; reason?: string }> = ({ text, reason }) => (
  <OutcomeLineText>
    <span>{text}</span>
    {reason && <OutcomeReason>{reason}</OutcomeReason>}
  </OutcomeLineText>
);

const ResultIcon: React.FC<{ failed: boolean }> = ({ failed }) => (
  <FeatherIcon
    icon={failed ? 'x-circle' : 'check-circle'}
    size={14}
    color={failed ? colors.performance.negative : colors.palette.Business_DataVizLilacDark}
    aria-hidden="true"
  />
);

// Shows what was saved and what failed when part of the invite failed.
const InviteOutcome: React.FC<IInviteOutcome> = ({
  outcome: { existingUsers, emailInvites },
  teamName,
}) => {
  const titleRef = useRef<HTMLHeadingElement>(null);
  // Move focus to the heading so screen readers announce the outcome.
  useEffect(() => titleRef.current?.focus(), []);
  return (
    <OutcomeContainer>
      <OutcomeTitle ref={titleRef} tabIndex={-1}>
        {INVITE_OUTCOME_COPY.TITLE}
      </OutcomeTitle>
      <OutcomeIntro>{INVITE_OUTCOME_COPY.INTRO}</OutcomeIntro>
      {existingUsers.length > 0 && (
        <section aria-label={INVITE_OUTCOME_COPY.EXISTING_USERS}>
          <OutcomeSectionTitle>{INVITE_OUTCOME_COPY.EXISTING_USERS}</OutcomeSectionTitle>
          <OutcomeList>
            {existingUsers.map(({ invite, changes }) => (
              <OutcomeRow key={invite.id}>
                <AvatarLetter avatar={invite.avatar} name={invite.name || invite.email} />
                <OutcomeUser>
                  <b>{invite.name || invite.email}</b>
                  {invite.name && <span>{invite.email}</span>}
                  <OutcomeLines>
                    {changes.map((change) => (
                      <OutcomeLine key={change.kind} $failed={change.result !== 'applied'}>
                        <ResultIcon failed={change.result !== 'applied'} />
                        <LineText
                          text={describeChange(change, teamName)}
                          reason={change.licenseError?.message}
                        />
                      </OutcomeLine>
                    ))}
                  </OutcomeLines>
                </OutcomeUser>
              </OutcomeRow>
            ))}
          </OutcomeList>
        </section>
      )}
      {emailInvites && (
        <section aria-label={INVITE_OUTCOME_COPY.NEW_USERS}>
          <OutcomeSectionTitle>{INVITE_OUTCOME_COPY.NEW_USERS}</OutcomeSectionTitle>
          <OutcomeLines>
            <OutcomeLine $failed={!emailInvites.sent}>
              <ResultIcon failed={!emailInvites.sent} />
              <LineText
                text={
                  emailInvites.sent
                    ? invitationsSentLine(emailInvites.count)
                    : invitationsNotSentLine(emailInvites.count)
                }
                reason={emailInvites.licenseError?.message}
              />
            </OutcomeLine>
          </OutcomeLines>
        </section>
      )}
    </OutcomeContainer>
  );
};

export default InviteOutcome;
